import {
    DocumentTypeEnum,
    Expense,
    ExpenseKindEnum,
    ExpenseRequestTypeEnum,
    ExpenseSplit,
    Hospitality,
    InboxInvoice,
    InvoiceSplit,
    Mileage,
    PerDiem,
    SplitTypeEnum,
} from "@finway-group/shared/lib/models"
import { ExpensePaymentFlowInformationEnum } from "@finway-group/shared/lib/models/expense/expensePaymentFlowInformation.enum"
import { Form } from "antd"
import { Store } from "antd/lib/form/interface"
import { parseCurrencyInput } from "Components/currencyInput/config"
import { useExpenseFormContext } from "Components/modals/expenseCreateForm.context"
import { buildNewApproverUpdateData, buildUpdateDataAccordingToSetting, initializeExpenseItemSplitsForForm } from "Features/pages/expenses/expenseUtils"
import { buildUnsetWorkflowUpdateData, buildWorkflowUpdateData, determineApplicableWorkflow } from "Features/pages/workflows/workflow.utils"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useCompany } from "Shared/hooks/company.hooks"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useExpense } from "Shared/hooks/expense.hooks"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { useVendorById, useVendors } from "Shared/hooks/vendor.hooks"
import { useWorkflows } from "Shared/hooks/workflow.hooks"
import { CostCenterService, VendorService } from "Shared/services"
import { isEmptyObject, isNotSet } from "Shared/utils/helpers"
import useUpdateEffect from "Shared/utils/hooks/useUpdateEffect"
import { fillFormWithOcrData } from "Shared/utils/ocr.utils"
import { InvoiceRequestForm } from "./invoiceRequest.form"
import PurchaseRequestForm from "./purchaseRequest.form"
import ReimbursementRequestForm from "./reimbursement.form"

interface ExpenseFormPropsInterface {
    onSubmit: (expense: Expense, extraData: { originalApproverId: string }) => void
    inboxOcrSource?: InboxInvoice
}

const ExpenseForm: React.FC<ExpenseFormPropsInterface> = ({ onSubmit, inboxOcrSource }) => {
    const { t } = useTranslation()
    const loggedInProfile = useLoggedInEmployeeProfile()
    const employees = useEmployees(true, false, true)
    const costCenters = useSubCostCenters(true)
    const vendors = useVendors(true)
    const workflows = useWorkflows()
    const taxes = useTaxes()
    const company = useCompany()

    //prettier-ignore
    const [ { expense, isNew, expenseForm, type, inboxInvoices, updateExpense, ocrSettings, isInvoiceRequest, isSubscription, isPreApprovedSubscription, setPrefilledVendorData, setIsOcrProcessed, setActiveDocumentsTab } ] = useExpenseFormContext()
    // add preset values, even if they were deleted. Otherwise it'll show the ID only.

    // approver at the moment of modal opening, to check if it has changed or not
    const [originalApproverId] = useState(expense.approvalNeededBy)

    if (expense.costCenter && !costCenters.find((cc) => cc._id === expense.costCenter)) costCenters.push(CostCenterService.getCostCenterById(expense.costCenter))
    if (expense.vendor && !vendors.find((vendor) => vendor.id === expense.vendor)) vendors.push(VendorService.getVendorById(expense.vendor))
    const vendor = useVendorById(expense.vendor)
    const paymentTerm = vendor?.rule?.paymentTerm

    const updateDueDateBasedOnRule = (invoiceDate: any) => {
        if (paymentTerm && invoiceDate) return { invoiceDueDate: moment(invoiceDate).add(paymentTerm, "days") }
        return {}
    }

    useEffect(() => {
        initializeExpenseItemSplitsForForm(expense)
        expenseForm.setFieldsValue({ ...expenseForm.getFieldsValue(), ...expense })
    }, [expense])

    useEffect(() => {
        if (inboxInvoices?.length && loggedInProfile.settings.enableOcr && inboxOcrSource) {
            const ocrResults = inboxOcrSource.ocrResults
            if (!isNotSet(ocrResults) && !isEmptyObject(ocrResults)) {
                fillFormWithOcrData(
                    expenseForm,
                    expense,
                    ocrResults,
                    vendors,
                    taxes,
                    updateExpense,
                    setPrefilledVendorData,
                    loggedInProfile.settings.globalCurrency,
                    ocrSettings.isOcrItemizationEnabled,
                    t
                )
                setIsOcrProcessed(true)
            }
        }
    }, [inboxInvoices?.length])

    useUpdateEffect(() => {
        if (expense.workflow && !expense.costCenter) return //quick fix until we consider splitted expenses on determineApplicableWorkflow, which will come after the workflow refactor
        const workflow = determineApplicableWorkflow(expense, workflows, isInvoiceRequest, loggedInProfile.settings.showGrossAmount)
        if (workflow && (isNotSet(expense.paymentFlowInformation) || expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_PAID)) {
            updateExpense(buildWorkflowUpdateData(expense, workflow))
        } else {
            updateExpense({
                ...buildUnsetWorkflowUpdateData(),
                ...buildNewApproverUpdateData(expense, workflows, employees, costCenters, loggedInProfile, isPreApprovedSubscription, isNew),
            } as any)
        }
    }, [
        expense.requestedBy,
        expense.costCenter,
        expense.costCenter2,
        expense.vendor,
        expense.totalNetPrice,
        expense.totalGrossPrice,
        expense.totalTaxPrice,
        expense.taxRate,
        expense.paymentFlowInformation,
    ])

    // update invoice due on if needed on vendor change

    useUpdateEffect(() => {
        updateExpense({
            ...buildUpdateDataAccordingToSetting(expense, vendors, employees, company),
            ...(updateDueDateBasedOnRule(expense.invoiceDate) as any),
        })
    }, [expense.vendor, expense.requestedBy])

    useUpdateEffect(() => {
        updateExpense(updateDueDateBasedOnRule(expense.invoiceDate) as any)
    }, [expense.invoiceDate])

    const onFinish = (form: Store) => {
        form.totalNetPrice = parseCurrencyInput(form.totalNetPrice)
        form.totalTaxPrice = parseCurrencyInput(form.totalTaxPrice)

        // Adjust split before submission.
        if (expense.splitType === SplitTypeEnum.ITEM && expense.splits.length > 0) {
            //If item split, parse the currency input properly.
            form.splits =
                form.splits?.map((itemSplit: ExpenseSplit) => ({
                    ...itemSplit,
                    netPrice: parseCurrencyInput(itemSplit.netPrice),
                    taxPrice: parseCurrencyInput(itemSplit.taxPrice),
                    grossPrice: parseCurrencyInput(itemSplit.grossPrice),
                })) ?? [] // TODO: how can this be empty?
        } else {
            // If invoice split or item is now empty, make it an invoice split and adjust according to percentage.
            form.splitType = SplitTypeEnum.SPLIT
            form.splits =
                form.splits?.map((invoiceSplit: InvoiceSplit) => ({
                    ...invoiceSplit,
                    taxRate: form.taxRate,
                    netPrice: (form.totalNetPrice * invoiceSplit.percentage) / 100,
                    taxPrice: (form.totalTaxPrice * invoiceSplit.percentage) / 100,
                    grossPrice: (form.totalGrossPrice * invoiceSplit.percentage) / 100,
                })) ?? [] // If form doesnt have split (perdiem/mileage, make it an empty array)
        }

        form.performancePeriodStartDate = form.performancePeriod?.[0]
        form.performancePeriodEndDate = form.performancePeriod?.[1]
        delete form.performancePeriod

        const offers = expenseForm.getFieldValue("offers")
        if (offers) form.offers = !isInvoiceRequest ? offers.map((offer: any) => offer.xhr || offer) : [...offers]

        // Transforming form data to be compatible with expense
        if (expense.kind === ExpenseKindEnum.HOSPITALITY) {
            form.attendees = form.attendees?.map(({ value, label }: any) => ({ internal: value !== label, user: value }))
        }

        const formData = {
            ...expense,
            ...form,
        }

        switch (form.kind) {
            case ExpenseKindEnum.HOSPITALITY:
                onSubmit(new Hospitality(formData), { originalApproverId })
                break
            case ExpenseKindEnum.MILEAGE:
                onSubmit(new Mileage(formData), { originalApproverId })
                break
            case ExpenseKindEnum.PER_DIEM:
                onSubmit(new PerDiem(formData), { originalApproverId })
                break
            default:
                onSubmit(new Expense(formData), { originalApproverId })
        }
    }

    const onFormFinishFailed = ({ errorFields }: any) => {
        if (isInvoiceRequest) {
            expenseForm.scrollToField(["invoiceNumber"])
            if (errorFields.some((errorField: any) => errorField.name.includes("invoices"))) {
                setActiveDocumentsTab(DocumentTypeEnum.INVOICE)
            }
        } else expenseForm.scrollToField(errorFields[0].name)
    }

    return (
        <Form form={expenseForm} layout="vertical" name="CreateExpenseForm" onFinish={onFinish} onFinishFailed={onFormFinishFailed} autoComplete="off">
            {type === ExpenseRequestTypeEnum.PURCHASE && <PurchaseRequestForm />}

            {type === ExpenseRequestTypeEnum.INVOICE && <InvoiceRequestForm />}

            {type === ExpenseRequestTypeEnum.REIMBURSEMENT && <ReimbursementRequestForm />}
        </Form>
    )
}

export default ExpenseForm
